import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import logger from "shared/utils/logging";
import { userDataReducer, initialUserData } from "shared/reducer/userdata/userDataReducerNew";
import { selectAllRobotClients } from "shared/reducer/userdata/selectors";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { servicesUrlConfig } from "shared/config/setting";
import {
  setRobotClients,
  setRobotConnected,
  setRobotDisconnected,  
  setUserRobotsData,
  updateRobotClientData,
} from "shared/reducer/userdata/userDataActions";
import {
  organizationsService,
  robotsService,
} from "features/administration/services";

const UserDataContext = createContext();

export function UserDataContextProvider({ children }) {
  
  const {selectedOrg, orgsLoading, rolesInSelectedOrg, hasRoleInSelectedOrg} = useUserOrg();

  const [robotsDataLoading, setRobotsDataLoading] = useState(false);
  const [robotClientsLoading, setRobotClientsLoading] = useState(false);
  const [error, setError] = useState("");
  const [userDataState, dispatch] = useReducer(
    userDataReducer,
    initialUserData,
  );
  
  const [isEnableOnlySelectedOrgRobots, setEnableOnlySelectedOrgRobots] = useState(true);

  const { notifyError } = useNotification();

  const { loading, isAuthenticated, authFetch } = useAuth();

  const history = useHistory();
  const location = useLocation();

  const loadRobotsData = useCallback(async () => {
    if (isAuthenticated) {
      setRobotsDataLoading(true);
      try{
        let data = null;
        if (!isEnableOnlySelectedOrgRobots) {          
          const response = await robotsService.getMe(authFetch, 1, 0); // fetch all robots of the user
          data = response.data;          
        }else if (selectedOrg) {
          const response = await organizationsService.getOrgRobots(selectedOrg.organization.org_id, authFetch, 1, 0);
          data = response.data;
        }else {
          data = { page: 1, per_page: 0, total: 0, results: [] };
        }  
        logger.debug("UserDataProviderNew: loadRobotsData fetched data:", data);      
        dispatch(setUserRobotsData(data));
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
          logger.error("Error fetching user's robots data", err);
          setError(errorMessage || "Failed to fetch user data");
          notifyError("Failed to fetch user data: " + errorMessage);
      } finally {
        setRobotsDataLoading(false);
      }
    } else {
      dispatch(setUserRobotsData(null));
    }
  }, [isAuthenticated, isEnableOnlySelectedOrgRobots, selectedOrg, authFetch, dispatch, notifyError]);

  const loadRobotClientsData = useCallback(async () => {
    if (isAuthenticated) {
      setRobotClientsLoading(true);
      const robotConnsURL =
        servicesUrlConfig.ROBOTS_CONNECTIONS_API.get_by_user;
      authFetch(robotConnsURL)
        .then(async (response) => {
          const data = response.data;
          dispatch(setRobotClients(data));
        })
        .catch((error) => {
          dispatch(setRobotClients([]));
          const errorMessage = error.response?.data?.message || error.message;
          logger.error("Error fetching robot channels list", error);
          setError(errorMessage || "Failed to fetch robot list");
          notifyError("Failed to fetch robot list: " + errorMessage);
        })
        .finally(() => {
          setRobotClientsLoading(false);
        });
    } else {
      // dispatch( { type: "SET_ROBOTLIST", payload: [] });
      dispatch(setRobotClients([]));
      setRobotClientsLoading(false);
    }
  }, [isAuthenticated, authFetch, dispatch, notifyError]);

  const isRobotOnline = useCallback((robot) => {
    const found = userDataState.robotClients.byId[robot.id];
    return found && found.status === "online";
  }, [userDataState]);

  const isRobotOnlineByRobotId = useCallback((robot_id) => {
    const found = selectAllRobotClients(userDataState).find(
      (rb) => rb.robot_id === robot_id,
    );
    return found && found.status === "online";
  }, [userDataState]);

  const hasCamera = useCallback((robot) => {
    const settings = robot?.setting || {};
    if (!settings?.cameras) return false;
    const cameras = settings?.cameras || [];
    for (let index = 0; index < cameras.length; index++) {
      const camera = cameras[index];
      if (camera?.class_name != "DummyCamera") {
        return true;
      }
    }
    return false;
  }, []);

  const getRobotClientCamerasSetting = useCallback((robot) => {
    const settings = robot?.setting || {};
    if (!settings?.cameras) return [];
    const cameras = settings?.cameras || [];
    return cameras;
  }, []);

  const getRobotClientSpeakersSetting = useCallback((robot) => {
    const settings = robot?.setting || {};
    if (!settings?.speakers) return [];
    const speakers = settings?.speakers || [];
    return speakers;
  }, []);

  const getRobotClientMicrophonesSetting = useCallback((robot) => {
    const settings = robot?.setting || {};
    if (!settings?.microphones) return [];
    const microphones = settings?.microphones || [];
    return microphones;
  }, []);

  const getCameraPeers = useCallback((robotGroup) => {
    return robotGroup?.connections?.filter((robot) => hasCamera(robot));
  }, [hasCamera]);

  const getConnectingUserClientByRole = useCallback((robotGroup, roleName) => {
    return (
      robotGroup?.connecting_user_clients
        ?.filter(([userClient, { state, role }]) => {
          return role == roleName;
        })
        .map(([userClient, { state, role }]) => {
          return {
            userClient,
            state,
            role,
          };
        }) || []
    );
  }, []);

  const getConnectingUserClientByState = useCallback((robotGroup, stateName) => {
    return (
      robotGroup?.connecting_user_clients
        ?.filter(([userClient, { state, role }]) => {
          return state == stateName;
        })
        .map(([userClient, { state, role }]) => {
          return {
            userClient,
            state,
            role,
          };
        }) || []
    );
  }, []);

  useEffect(() => {
    if (loading || orgsLoading) return;
    loadRobotsData();
    loadRobotClientsData();
  }, [loading, loadRobotsData, loadRobotClientsData]);

  const handleRobotClientStatusChanged = useCallback((evt, status) => {
    if (status.toLowerCase() === "online")
      dispatch(setRobotConnected(evt));
    else dispatch(setRobotDisconnected(evt));
  }, [dispatch]);

  const updateRobotClientConnectionData = useCallback((robotClientId, updatedData) => {
    dispatch(
      updateRobotClientData(robotClientId, updatedData),
    );
  }, [dispatch]);

  // Using normalized state
  const organizations = userDataState.organizations;
  const robots = userDataState.robots;
  const robotClients = selectAllRobotClients(userDataState);
  const robotClientGroups = userDataState.robotClientGroups || {};

  const robotListLoading =
    robotsDataLoading || orgsLoading || robotClientsLoading;

  return (
    <UserDataContext.Provider
      value={{
        error,
        dispatch,
        isRobotOnline,
        getCameraPeers,
        getConnectingUserClientByRole,
        getConnectingUserClientByState,
        handleRobotClientStatusChanged,
        updateRobotClientConnectionData,
        isRobotOnlineByRobotId,
        hasCamera,
        getRobotClientCamerasSetting,
        getRobotClientSpeakersSetting,
        getRobotClientMicrophonesSetting,
        setEnableOnlySelectedOrgRobots,
        rolesInSelectedOrg,
        hasRoleInSelectedOrg,
        selectedOrg,
        isEnableOnlySelectedOrgRobots,
        robotListLoading,
        organizations,
        robots,
        robotClients,
        robotClientGroups,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
}

export const useUserData = () => useContext(UserDataContext);
